import * as React from "react";
import { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Button } from "components/bootstrap";
import { PaginatedList, Spinner } from "components/common";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import SliceFilters, {
  ALPHABETICAL_SORT,
  defaultSortDirectionForMode
} from "./SliceFilters";
import SliceList from "./SliceList";
import useSlices from "./useSlices";
const EmptySlicesHeader = styled.div(
  ({ theme }) => css`
    margin: ${theme.spacings.sm} 0 ${theme.spacings.xs};
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacings.xs};
    color: ${theme.colors.gray[50]};
    font-size: ${theme.fonts.size.small};
  `
);
const EmptySlicesLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;
const SlicesLists = styled.div`
  max-height: 700px;
  overflow: auto;
`;
const SLICES_PAGE_SIZE = 10;
const SHOW_EMPTY_SLICES_SECTION = false;
const paginatedSlices = (slices, page, pageSize) => {
  const from = (page - 1) * pageSize;
  return slices.slice(from, from + pageSize);
};
const useAutoExpandEmptySlices = ({
  activeSlice,
  showEmptySlices,
  visibleEmptySlices,
  setShowEmptySlices,
  setEmptyPage
}) => {
  const lastAutoExpandedSliceRef = useRef(void 0);
  useEffect(() => {
    if (!activeSlice || showEmptySlices) {
      return;
    }
    const activeSliceValue = String(activeSlice);
    const activeSliceMovedToEmpty = visibleEmptySlices.some((slice) => String(slice.value) === activeSliceValue);
    if (!activeSliceMovedToEmpty) {
      lastAutoExpandedSliceRef.current = void 0;
      return;
    }
    if (lastAutoExpandedSliceRef.current === activeSliceValue) {
      return;
    }
    lastAutoExpandedSliceRef.current = activeSliceValue;
    setShowEmptySlices(true);
    setEmptyPage(1);
  }, [activeSlice, showEmptySlices, visibleEmptySlices, setShowEmptySlices, setEmptyPage]);
};
const SlicesOverview = ({
  appSection,
  sliceCol,
  activeSlice,
  activeColumnTitle,
  onChangeSlicing,
  sliceRenderers = void 0,
  fetchSlices,
  sortOptions,
  defaultSliceSort = void 0,
  onSlicingPreferencesChange,
  slicingPreferences = void 0
}) => {
  var _a, _b;
  const [showEmptySlices, setShowEmptySlices] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [nonEmptyPage, setNonEmptyPage] = useState(1);
  const [emptyPage, setEmptyPage] = useState(1);
  const preferredSortMode = slicingPreferences == null ? void 0 : slicingPreferences.sortBy;
  const sortMode = preferredSortMode && sortOptions.some((option) => option.value === preferredSortMode) ? preferredSortMode : (_a = defaultSliceSort == null ? void 0 : defaultSliceSort.mode) != null ? _a : ALPHABETICAL_SORT;
  const sortDirection = (_b = slicingPreferences == null ? void 0 : slicingPreferences.order) != null ? _b : defaultSortDirectionForMode(sortMode, defaultSliceSort);
  const sendTelemetry = useSendTelemetry();
  const { isLoading, refetchSlices, hasEmptySlices, emptySliceCount, visibleNonEmptySlices, visibleEmptySlices } = useSlices({
    fetchSlices,
    activeSlice,
    searchQuery,
    sortDirection,
    sortMode,
    sliceRenderers
  });
  useAutoExpandEmptySlices({ activeSlice, showEmptySlices, visibleEmptySlices, setShowEmptySlices, setEmptyPage });
  const currentNonEmptySlices = paginatedSlices(visibleNonEmptySlices, nonEmptyPage, SLICES_PAGE_SIZE);
  const currentEmptySlices = paginatedSlices(visibleEmptySlices, emptyPage, SLICES_PAGE_SIZE);
  const onSearchQueryChange = (newQuery) => {
    setSearchQuery(newQuery);
    setNonEmptyPage(1);
    setEmptyPage(1);
  };
  const onSortModeUpdate = (mode) => {
    const direction = defaultSortDirectionForMode(mode, defaultSliceSort);
    setNonEmptyPage(1);
    setEmptyPage(1);
    if (sliceCol) {
      onSlicingPreferencesChange({
        sliceColumn: sliceCol,
        sortBy: mode,
        order: direction,
        readOnly: slicingPreferences == null ? void 0 : slicingPreferences.readOnly
      });
    }
  };
  const onSortDirectionUpdate = (direction) => {
    setNonEmptyPage(1);
    setEmptyPage(1);
    if (sliceCol) {
      onSlicingPreferencesChange({
        sliceColumn: sliceCol,
        sortBy: sortMode,
        order: direction,
        readOnly: slicingPreferences == null ? void 0 : slicingPreferences.readOnly
      });
    }
  };
  const onToggleEmptySlices = () => {
    setShowEmptySlices((current) => {
      const next = !current;
      sendTelemetry(TELEMETRY_EVENT_TYPE.ENTITY_DATA_TABLE.SLICE_EMPTY_VALUES_TOGGLED, {
        app_section: appSection,
        event_details: {
          attribute_id: sliceCol,
          show_empty_slices: next
        }
      });
      if (next) {
        setEmptyPage(1);
      }
      return next;
    });
  };
  const onSliceSelection = (newSliceCol, newSlice) => {
    onChangeSlicing(newSliceCol, newSlice);
    if (newSlice !== void 0 && newSlice !== activeSlice) {
      void refetchSlices();
    }
  };
  if (isLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    SliceFilters,
    {
      appSection,
      activeColumnTitle,
      sliceCol,
      searchQuery,
      onSearchQueryChange,
      onSearchReset: () => onSearchQueryChange(""),
      sortOptions,
      sortMode,
      onSortModeChange: onSortModeUpdate,
      sortDirection,
      onSortDirectionChange: onSortDirectionUpdate
    }
  ), /* @__PURE__ */ React.createElement(SlicesLists, null, /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      activePage: nonEmptyPage,
      pageSize: SLICES_PAGE_SIZE,
      totalItems: visibleNonEmptySlices.length,
      showPageSizeSelect: false,
      hideFirstAndLastPageLinks: true,
      useQueryParameter: false,
      onChange: (newPage, pageSize) => {
        void pageSize;
        setNonEmptyPage(newPage);
      }
    },
    /* @__PURE__ */ React.createElement(
      SliceList,
      {
        slices: currentNonEmptySlices,
        activeSlice,
        sliceCol,
        onChangeSlicing: onSliceSelection,
        sliceRenderers,
        listTestId: "slices-list"
      }
    )
  ), SHOW_EMPTY_SLICES_SECTION && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(EmptySlicesHeader, null, hasEmptySlices ? /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "link",
      bsSize: "sm",
      onClick: onToggleEmptySlices,
      title: showEmptySlices ? "Hide empty slices" : "Show empty slices"
    },
    showEmptySlices ? "Hide empty slices" : "Show empty slices",
    " (",
    emptySliceCount,
    ")"
  ) : /* @__PURE__ */ React.createElement(EmptySlicesLabel, null, "Empty slices (0)")), showEmptySlices && visibleEmptySlices.length > 0 && /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      activePage: emptyPage,
      hideFirstAndLastPageLinks: true,
      pageSize: SLICES_PAGE_SIZE,
      totalItems: visibleEmptySlices.length,
      showPageSizeSelect: false,
      useQueryParameter: false,
      onChange: (newPage, pageSize) => {
        void pageSize;
        setEmptyPage(newPage);
      }
    },
    /* @__PURE__ */ React.createElement(
      SliceList,
      {
        slices: currentEmptySlices,
        activeSlice,
        sliceCol,
        onChangeSlicing: onSliceSelection,
        sliceRenderers,
        keyPrefix: "empty-",
        listTestId: "empty-slices-list"
      }
    )
  ))));
};
export default SlicesOverview;
